import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  TrendingUp, 
  Target, 
  ArrowRight,
  BarChart3,
  DollarSign,
  Users,
  Zap,
  Award,
  ShoppingCart,
  Smartphone,
  Heart
} from "lucide-react";

const CaseStudies = () => {
  const caseStudies = [
    {
      company: "EcoBeauty",
      industry: "E-commerce Beauty",
      logo: "🌿",
      challenge: "Struggling with 1.5x ROAS and high customer acquisition costs across Meta and Google Ads.",
      solution: "Implemented AI-powered creative testing framework with UGC video content and advanced audience segmentation.",
      results: [
        { metric: "ROAS", before: "1.5x", after: "4.8x", improvement: "+220%" },
        { metric: "CPA", before: "$45", after: "$18", improvement: "-60%" },
        { metric: "Revenue", before: "$50K/mo", after: "$240K/mo", improvement: "+380%" },
        { metric: "AOV", before: "$35", after: "$52", improvement: "+49%" }
      ],
      timeline: "3 months",
      image: "https://images.unsplash.com/photo-1758518729706-b1810dd39cc6?w=600&auto=format&fit=crop&q=80",
      testimonial: "GrowthPro transformed our marketing. We went from struggling with 1.5x ROAS to consistently hitting 4-5x ROAS within 3 months.",
      author: "Sarah Chen, CEO"
    },
    {
      company: "TechStart Solutions",
      industry: "B2B SaaS",
      logo: "💻",
      challenge: "Low conversion rates and expensive video production costs limiting scale and growth potential.",
      solution: "Deployed AI video creation system with personalized demo videos and LinkedIn advertising optimization.",
      results: [
        { metric: "Conversion Rate", before: "2.1%", after: "6.8%", improvement: "+224%" },
        { metric: "Cost per Lead", before: "$120", after: "$42", improvement: "-65%" },
        { metric: "MRR Growth", before: "$25K", after: "$85K", improvement: "+240%" },
        { metric: "Video Production Cost", before: "$5K/video", after: "$200/video", improvement: "-96%" }
      ],
      timeline: "4 months",
      image: "https://images.unsplash.com/photo-1757845701047-69080f6b1139?w=600&auto=format&fit=crop&q=80",
      testimonial: "Their AI-powered video creation saved us thousands while increasing our conversion rates by 180%. Game-changing partnership.",
      author: "Marcus Rodriguez, Marketing Director"
    },
    {
      company: "Wellness Co.",
      industry: "Health & Wellness",
      logo: "🧘",
      challenge: "Seasonal revenue fluctuations and difficulty scaling beyond local market reach.",
      solution: "Multi-platform growth strategy with TikTok expansion, influencer partnerships, and retention optimization.",
      results: [
        { metric: "Revenue Growth", before: "$100K/mo", after: "$420K/mo", improvement: "+320%" },
        { metric: "Customer LTV", before: "$85", after: "$165", improvement: "+94%" },
        { metric: "Market Reach", before: "Local", after: "National", improvement: "10x expansion" },
        { metric: "Retention Rate", before: "35%", after: "68%", improvement: "+94%" }
      ],
      timeline: "6 months",
      image: "https://images.unsplash.com/photo-1758518731468-98e90ffd7430?w=600&auto=format&fit=crop&q=80",
      testimonial: "Finally, an agency that actually understands performance marketing. Transparent, data-driven, and results-focused.",
      author: "Jennifer Walsh, Founder"
    }
  ];

  const metrics = [
    { icon: <TrendingUp className="h-8 w-8" />, label: "Average ROAS Increase", value: "300%" },
    { icon: <DollarSign className="h-8 w-8" />, label: "Total Revenue Generated", value: "$50M+" },
    { icon: <Users className="h-8 w-8" />, label: "Campaigns Optimized", value: "500+" },
    { icon: <Award className="h-8 w-8" />, label: "Client Success Rate", value: "98%" }
  ];

  const industries = [
    { name: "E-commerce", icon: <ShoppingCart className="h-6 w-6" />, count: "150+ clients" },
    { name: "SaaS & Tech", icon: <Smartphone className="h-6 w-6" />, count: "80+ clients" },
    { name: "Health & Wellness", icon: <Heart className="h-6 w-6" />, count: "60+ clients" },
    { name: "Professional Services", icon: <Target className="h-6 w-6" />, count: "40+ clients" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-6">
              <Award className="h-3 w-3 mr-1" />
              Proven Success Stories
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-gradient">Case Studies</span> & Results
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8">
              Real businesses, real results. See how we've helped brands achieve 
              breakthrough growth with our data-driven performance marketing strategies.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="pb-20 lg:pb-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {metrics.map((metric, index) => (
              <Card key={index} className="card-elegant text-center">
                <CardContent className="p-8">
                  <div className="text-primary mb-4 flex justify-center">
                    {metric.icon}
                  </div>
                  <div className="text-3xl font-bold text-gradient mb-2">
                    {metric.value}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {metric.label}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="pb-20 lg:pb-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {caseStudies.map((study, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}>
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-3 mb-6">
                    <span className="text-3xl">{study.logo}</span>
                    <div>
                      <h2 className="text-2xl font-bold">{study.company}</h2>
                      <Badge variant="outline">{study.industry}</Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-2">Challenge</h3>
                      <p className="text-muted-foreground">{study.challenge}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-2">Solution</h3>
                      <p className="text-muted-foreground">{study.solution}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-4">Results in {study.timeline}</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {study.results.map((result, resultIndex) => (
                          <div key={resultIndex} className="bg-muted/50 rounded-lg p-4">
                            <div className="text-sm text-muted-foreground mb-1">{result.metric}</div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm line-through text-muted-foreground">{result.before}</span>
                              <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              <span className="font-semibold">{result.after}</span>
                            </div>
                            <div className="text-sm text-primary font-medium">{result.improvement}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-lg">
                      <p className="italic mb-3">\"{study.testimonial}\"</p>
                      <div className="text-sm font-medium">— {study.author}</div>
                    </div>
                  </div>
                </div>
                
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <div className="relative">
                    <img 
                      src={study.image} 
                      alt={`${study.company} case study`}
                      className="rounded-2xl shadow-2xl w-full"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent rounded-2xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Industries We <span className="text-gradient">Serve</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Proven success across diverse industries and business models.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <Card key={index} className="card-elegant text-center">
                <CardContent className="p-8">
                  <div className="text-primary mb-4 flex justify-center">
                    {industry.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{industry.name}</h3>
                  <p className="text-sm text-muted-foreground">{industry.count}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="card-elegant bg-gradient-to-r from-primary/10 to-primary-glow/10 border-primary/20">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-5xl font-bold mb-6">
                Ready to Be Our <span className="text-gradient">Next Success Story</span>?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join hundreds of successful brands who've achieved breakthrough growth with our proven strategies.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="btn-glow text-lg px-8 py-6">
                  <Link to="/contact">
                    Get Your Growth Plan
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6">
                  <Link to="/process">
                    See Our Process
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CaseStudies;